import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock3, MapPin, Package, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ProductCard from "@/components/product/ProductCard";
import { colorRules } from "@/data/products";

// 🔥 IMPORT YOUR POSTERS
import banner1 from "../components/assets/image1.png";
import banner2 from "../components/assets/image2.png";
import banner3 from "../components/assets/image3.png";
import banner4 from "../components/assets/image4.png";
import banner5 from "../components/assets/image5.png";


const banners = [banner1, banner2, banner3, banner4, banner5];

const trustBadges = [
  { icon: Clock3, text: "28+ Years Experience" },
  { icon: MapPin, text: "Pan India Supply" },
  { icon: ShieldCheck, text: "Quality Assured" },
  { icon: Package, text: "Bulk Wholesale" },
];

export default function HomePage({ company, products }) {
  const [current, setCurrent] = useState(0);

  // 🔥 AUTO SLIDER
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % banners.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const featuredProducts = products.slice(0, 6);

  return (
    <div className="space-y-16 md:space-y-24">

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 lg:grid-cols-2">

          {/* LEFT CONTENT */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-5 bg-orange-500 text-white">
              Networking Accessories & Telecom Products
            </Badge>

            <h1 className="text-4xl font-bold sm:text-5xl lg:text-6xl">
              Complete B2B Networking Catalog for Fast Business Procurement
            </h1>

            <p className="mt-5 text-slate-200">
              Explore routers, fiber optics, racks, and telecom tools.
              Send product inquiries instantly.
            </p>

            <div className="mt-8 flex gap-3">
              <Button asChild className="bg-orange-500 hover:bg-orange-600">
                <Link to="/products">
                  Explore Products <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>

              <Button asChild variant="outline">
                <Link to="/contact">Contact Team</Link>
              </Button>
            </div>
          </motion.div>

          {/* RIGHT SLIDER */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="aspect-[4/3] overflow-hidden rounded-sm border border-slate-700">
              <img
                src={banners[current]}
                className="h-full w-full object-cover transition duration-500"
                alt="banner"
              />
            </div>

            {/* DOTS */}
            <div className="mt-3 flex justify-center gap-2">
              {banners.map((_, i) => (
                <div
                  key={i}
                  className={`h-2 w-2 rounded-full ${
                    i === current ? "bg-orange-500" : "bg-gray-400"
                  }`}
                />
              ))}
            </div>

            <p className="mt-4 text-sm text-slate-300">
              {company.name} • {company.phone} • GST: {company.gst}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ================= TRUST ================= */}
      <section className="max-w-7xl mx-auto px-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {trustBadges.map((badge) => {
            const Icon = badge.icon;
            return (
              <div className="flex items-center gap-3 border p-4 bg-white">
                <Icon className="text-orange-500" />
                <p className="font-semibold">{badge.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ================= PROMO BANNERS ================= */}
      <section className="max-w-7xl mx-auto px-4">
        <h2 className="text-2xl font-bold">Latest Products & Offers</h2>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {banners.map((img, i) => (
            <img key={i} src={img} className="rounded-sm w-full" />
          ))}
        </div>
      </section>

      {/* ================= FEATURED PRODUCTS ================= */}
      <section className="max-w-7xl mx-auto px-4">
        <h2 className="text-3xl font-bold">Featured Products</h2>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
          {featuredProducts.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>
      </section>

      {/* ================= WHY CHOOSE US ================= */}
      <section className="max-w-7xl mx-auto px-4">
        <div className="bg-white border p-8">
          <h2 className="text-3xl font-bold">Why Choose HEXA INFOTECH</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
            {[
              "Authorized Suppliers",
              "Bulk Pricing",
              "Fast Delivery",
              "Genuine Products",
              "Support Team",
            ].map((item) => (
              <div className="bg-slate-50 p-4 border text-center font-semibold">
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}